import * as borogove from "borogove";
import {
	Alert,
	Autocomplete,
	Switch,
	FormControl,
	FormControlLabel,
	MenuItem,
	IconButton,
	TextField,
	Stack,
	InputAdornment,
} from "@mui/material";
import {
	MaterialReactTable,
	useMaterialReactTable,
} from "material-react-table";
import {
	useId,
	useState,
	useMemo,
	useImperativeHandle,
	forwardRef,
} from "react";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const inputType = (field: borogove.FormField) => {
	if (field.type == "text-private") return "password";
	if (field.datatype == "boolean") return "checkbox";
	if (field.datatype.startsWith("html:")) return field.datatype.slice(5);
	if (field.datatype == "xs:anyURI") return "url";
	if (field.datatype == "xs:dateTime") return "datetime-local";
	if (field.datatype == "xs:date") return "date";
	if (field.datatype == "xs:time") return "time";
	if (
		[
			"xs:byte",
			"xs:decimal",
			"xs:double",
			"xs:int",
			"xs:integer",
			"xs:long",
			"xs:short",
		].includes(field.datatype)
	) {
		if (field.rangeMin && field.rangeMax) return "range";
		return "number";
	}
	return "text";
};

const openOptionValue = (v: borogove.FormOption | string | null): string => {
	if (!v) return "";
	if (typeof v === "string") return v;

	return v.value ?? "";
};

const ResultField = forwardRef(
	({ field }: { field: borogove.FormField }, ref) => {
		useImperativeHandle(ref, () => ({ focus: () => {}, value: field.value }), [
			field.value,
		]);

		return (
			<div>
				{field.value.map((v, idx) =>
					field.datatype === "xs:anyURI" ? (
						<a key={idx} href={v} className="value">
							{v}
						</a>
					) : (
						<p key={idx} className="value">
							{v}
						</p>
					),
				)}
			</div>
		);
	},
);
ResultField.displayName = "ResultField";

const FormField = ({
	field,
	isResult,
}: {
	field: borogove.FormField;
	isResult: boolean;
}) => {
	const [autocompleteValue, setAutocompleteValue] = useState(
		field.value.join("\n"),
	);
	const [multiAutocompleteValue, setMultiAutocompleteValue] = useState(
		field.value,
	);
	const [showPassword, setShowPassword] = useState(false);
	const datalistId = useId();
	const selectId = useId();
	const step = ["xs:decimal", "xs:double"].includes(field.datatype)
		? "any"
		: undefined;

	if (field.type == "boolean") {
		return (
			<FormControl fullWidth>
				<FormControlLabel
					control={
						<Switch
							name={field.name}
							defaultChecked={field.value[0] === "true"}
						/>
					}
					label={field.label}
					title={field.desc ?? undefined}
					required={field.required}
				/>
			</FormControl>
		);
	}

	const multiple = field.type === "list-multi";
	if (["list-single", "list-multi"].includes(field.type) && field.open) {
		const value = field.value.join("\n");
		return multiple ? (
			<>
				{multiAutocompleteValue.map((value, idx) => (
					<input key={idx} name={field.name} type="hidden" value={value} />
				))}
				<Autocomplete<borogove.FormOption | string, true, false, true>
					fullWidth
					freeSolo
					multiple
					handleHomeEndKeys
					forcePopupIcon
					autoSelect
					defaultValue={
						field.options.filter((o) => field.value.includes(o.value ?? "")) ??
						field.value
					}
					options={field.options}
					onChange={(e, newValue) =>
						setMultiAutocompleteValue(newValue.map(openOptionValue))
					}
					renderInput={(params) => (
						<TextField {...params} label={field.label} />
					)}
				/>
			</>
		) : (
			<>
				<input name={field.name} type="hidden" value={autocompleteValue} />
				<Autocomplete<borogove.FormOption | string, false, false, true>
					fullWidth
					freeSolo
					handleHomeEndKeys
					forcePopupIcon
					autoSelect
					defaultValue={field.options.find((o) => o.value === value) ?? value}
					options={field.options}
					onChange={(e, newValue) => {
						if (typeof newValue === "string") {
							const o = field.options.find((o) => o.label === newValue);
							// Sometimes it sends us the label even if it matches a selection?
							if (o) newValue = o;
						}
						setAutocompleteValue(openOptionValue(newValue));
					}}
					renderInput={(params) => (
						<TextField {...params} label={field.label} />
					)}
				/>
			</>
		);
	}

	const readOnly = isResult || field.type == "fixed";
	if (multiple) {
		// Terrible hack: https://github.com/mui/material-ui/issues/48678
		setTimeout(() => {
			const el = document.querySelector(
				`#${CSS.escape(selectId)} ~ input.MuiSelect-nativeInput`,
			);
			if (el) el.parentNode?.removeChild(el);
		}, 200);
	}
	return (
		<>
			<TextField
				slots={readOnly ? { htmlInput: ResultField } : {}}
				label={field.label}
				helperText={field.desc}
				name={field.name}
				defaultValue={multiple ? field.value : field.value.join("\n")}
				fullWidth
				multiline={field.type == "text-multiline" || field.value.length > 1}
				required={field.required}
				type={showPassword ? "text" : inputType(field)}
				select={!readOnly && ["list-single", "list-multi"].includes(field.type)}
				onChange={(e) => {
					const value = e.target.value;
					setMultiAutocompleteValue(Array.isArray(value) ? value : [value]);
				}}
				slotProps={{
					htmlInput: {
						step,
						multiple: ["list-multi", "text-multi"].includes(field.type),
						inputMode: field.datatype === "jid" ? "email" : undefined,
						pattern: field.regex ?? undefined,
						min: field.rangeMin ?? undefined,
						max: field.rangeMax ?? undefined,
						list: datalistId,
						field: field,
					},
					select: {
						multiple,
						id: selectId,
					},
					input: {
						readOnly,
						endAdornment:
							field.type == "text-private" ? (
								<InputAdornment position="end">
									<IconButton
										title={
											showPassword
												? "Hide the password"
												: "Display the password"
										}
										onClick={() => setShowPassword((show) => !show)}
										edge="end"
									>
										{showPassword ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								</InputAdornment>
							) : undefined,
					},
				}}
			>
				{["list-single", "list-multi"].includes(field.type) &&
					field.options.map((option) => (
						<MenuItem key={option.value} value={option.value ?? undefined}>
							{option.label ?? option.value}
						</MenuItem>
					))}
			</TextField>
			{!["list-single", "list-multi"].includes(field.type) && (
				<datalist id={datalistId}>
					{field.options.map((option) => (
						<option key={option.value} value={option.value ?? undefined}>
							{option.label ?? option.value}
						</option>
					))}
				</datalist>
			)}
			{multiple &&
				multiAutocompleteValue.map((value, idx) => (
					<input key={idx} name={field.name} type="hidden" value={value} />
				))}
		</>
	);
};

export const FormTable = ({
	header,
	rows,
}: {
	header: borogove.FormField[];
	rows: borogove.FormField[][];
}) => {
	"use no memo";

	// have to use no memo or MaterialReactTable breaks
	// So we memo manually here

	const autocomplete = "autocomplete" as const;

	const columns = useMemo(
		() =>
			header.map((field) => ({
				id: field.name,
				header: field.label ?? field.name,
				filterVariant: autocomplete,
				accessorFn: (row: borogove.FormField[]) =>
					row.find((f) => f.name == field.name)?.value?.join("\n"),
			})),
		[header],
	);

	// max height required for sticky header
	const table = useMaterialReactTable({
		columns,
		data: rows,
		enableBottomToolbar: false,
		enableClickToCopy: true,
		enableFacetedValues: true,
		enablePagination: false,
		enableStickyHeader: true,
		enableDensityToggle: false,
		initialState: { density: "compact" },
		muiTableContainerProps: { sx: { maxHeight: "calc(100vh - 21em)" } },
	});

	return <MaterialReactTable table={table} />;
};

export const FormSection = ({
	section,
	isResult,
}: {
	section: borogove.FormSection;
	isResult?: boolean;
}) => {
	return (
		<section>
			<Stack spacing={2}>
				{section.title() && <h1>{section.title()}</h1>}
				{section.items().map((item) => (
					<>
						{item.text && item.status === "success" && (
							<Alert severity="success">{item.text}</Alert>
						)}
						{item.text && item.status === "info" && (
							<Alert severity="info">{item.text}</Alert>
						)}
						{item.text && item.status === "warn" && (
							<Alert severity="warning">{item.text}</Alert>
						)}
						{item.text && item.status === "error" && (
							<Alert severity="error">{item.text}</Alert>
						)}
						{item.text && !item.status && <p>{item.text}</p>}
						{item.field && (
							<FormField field={item.field} isResult={isResult ?? false} />
						)}
						{item.tableHeader && item.tableRows && (
							<FormTable header={item.tableHeader} rows={item.tableRows} />
						)}
						{item.section && (
							<FormSection
								section={item.section}
								isResult={isResult ?? false}
							/>
						)}
					</>
				))}
			</Stack>
		</section>
	);
};
